/**
 * Tags — the person's own filing, and the only axis on a person's page that
 * is theirs alone. A GROUP is a tag: "family", "work", "the tuesday group".
 *
 * The suggestion list is derived from the tags actually in use (a DISTINCT
 * over the column, server-side), so a tag can never exist in a picker and
 * nowhere else — and typing a new one is the same gesture as picking an old
 * one, because making people choose between "create" and "select" is how tag
 * UIs get abandoned.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Tag, X } from 'lucide-react'

export function TagChips({ tags, onClick, active }: {
  tags: string[]
  onClick?: (tag: string) => void
  active?: (tag: string) => boolean
}) {
  if (!tags.length) return null
  return (
    <div className="flex flex-wrap gap-1.5">
      {tags.map((t) => (
        <button
          key={t}
          onClick={onClick ? () => onClick(t) : undefined}
          disabled={!onClick}
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs
                      transition-colors disabled:cursor-default
                      ${active?.(t)
                        ? 'bg-brand text-white'
                        : 'bg-surface-alt text-secondary hover:bg-brand/10'}`}
        >
          <Tag className="w-3 h-3" /> {t}
        </button>
      ))}
    </div>
  )
}

export function TagEditor({ value, suggestions, onChange, busy }: {
  value: string[]
  suggestions: string[]
  onChange: (tags: string[]) => void
  busy?: boolean
}) {
  const [draft, setDraft] = useState('')
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement | null>(null)

  const matches = useMemo(() => {
    const have = new Set(value.map((t) => t.toLowerCase()))
    const q = draft.trim().toLowerCase()
    return suggestions
      .filter((t) => !have.has(t.toLowerCase()) && (!q || t.toLowerCase().includes(q)))
      .slice(0, 8)
  }, [draft, suggestions, value])

  useEffect(() => {
    const away = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', away)
    return () => document.removeEventListener('mousedown', away)
  }, [])

  const add = (tag: string) => {
    const t = tag.trim()
    if (!t) return
    // Case-insensitive: "Work" and "work" are one tag, and whichever spelling
    // is already on the person wins so the list does not fragment.
    if (value.some((x) => x.toLowerCase() === t.toLowerCase())) { setDraft(''); return }
    onChange([...value, t])
    setDraft('')
  }

  return (
    <div ref={box} className="relative">
      <div className="flex flex-wrap items-center gap-1.5">
        {value.map((t) => (
          <span key={t}
                className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full
                           bg-brand/10 text-xs text-brand">
            <Tag className="w-3 h-3" /> {t}
            <button onClick={() => onChange(value.filter((x) => x !== t))}
                    disabled={busy}
                    aria-label={`Remove tag ${t}`}
                    className="p-0.5 rounded-full hover:bg-brand/20 disabled:opacity-50">
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => { setDraft(e.target.value); setOpen(true) }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(draft) }
            if (e.key === 'Backspace' && !draft && value.length) onChange(value.slice(0, -1))
          }}
          disabled={busy}
          placeholder={value.length ? 'add a tag' : 'family, work, the tuesday group…'}
          className="flex-1 min-w-[10rem] bg-transparent text-xs text-primary
                     placeholder:text-muted focus:outline-none py-1"
        />
      </div>
      {open && matches.length > 0 && (
        <div className="absolute z-20 mt-1 w-56 rounded border border-soft bg-surface shadow-lg py-1">
          {matches.map((t) => (
            <button key={t} onClick={() => { add(t); setOpen(false) }}
                    className="w-full text-left px-3 py-1.5 text-xs text-secondary hover:bg-surface-alt">
              {t}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
